import React, { useEffect, useState } from "react";
import { _t } from "app/i18n/i18n";
import { Form, Button } from "react-bootstrap";
import { useQueryParams } from "lib/hooks/useQueryParams";
import { push, setParam } from "api/search/filterUrlMaker";
import { features } from "app/features";
import { AutoComplete } from "../../redesign/menuFilterBar/parts/AutoComplete";
import { useFetchAutocomplete } from "../../redesign/menuFilterBar/api/useGetPeriodicals";
import { tryDoFetch } from "../../../lib/tryDoFetch";
import { miscSlice } from "../../../redux/slices/miscSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  ALL_AUTOCOMPLETE_URL,
  ALL_THEMES,
  EVENT_THEME,
  INITIAL_THEMES_FILTER,
  THEME_SUBTHEME,
} from "../constants/eventsConstant";
import { useDebounce } from "../../../lib/hooks/useDebounce";

export const DateFilterControls = ({ pastEventDate }) => {
  const qsDate = useQueryParams().eventDate || pastEventDate;
  const setDate = (ev) => push(setParam("eventDate", ev.target.value));
  const isActiveClass = (date) => (date === qsDate ? "dark " : "outline-dark ");

  return (
    <section>
      <Button value="1month" variant={isActiveClass("1month") + "mr-3"} onClick={setDate}>
        {_t("Last 30 days")}
      </Button>
      <Button value="ytd" variant={isActiveClass("ytd") + "mr-3"} onClick={setDate}>
        {_t("Year to date")}
      </Button>
      <Button value="1year" variant={isActiveClass("1year") + "mr-3"} onClick={setDate}>
        {_t("One year")}
      </Button>
    </section>
  );
};

export const LanguageLocationFilterControls = () => {
  if (features.eventFilters.showLocationFilters && features.eventFilters.showLanguageFilters)
    return (
      <aside className="d-flex gap">
        <LanguageDropdown />
        <LocationDropdown />
      </aside>
    );
  else if (!features.eventFilters.showLocationFilters && features.eventFilters.showLanguageFilters)
    return (
      <aside className="d-flex gap">
        <LanguageDropdown />
      </aside>
    );
  else if (features.eventFilters.showLocationFilters && !features.eventFilters.showLanguageFilters)
    return (
      <aside className="d-flex gap">
        <LocationDropdown />
      </aside>
    );
  else return null;
};

const LanguageDropdown = () => {
  const qsLanguage = useQueryParams().eventLanguage || "";
  const setLanguage = (ev) => push(setParam("eventLanguage", ev.target.value));

  return (
    <Form.Control size="sm" as="select" defaultValue={qsLanguage} onChange={setLanguage}>
      <option value="">{_t("All Languages")}</option>
      <option value="English">{_t("English")}</option>
      <option value="Mandarin">{_t("Chinese (Mandarin)")}</option>
    </Form.Control>
  );
};

const LocationDropdown = () => {
  const qsLocation = useQueryParams().eventLocation || "";
  const setLocation = (ev) => push(setParam("eventLocation", ev.target.value));

  return (
    <Form.Control size="sm" as="select" defaultValue={qsLocation} onChange={setLocation}>
      <option value="">{_t("All Locations")}</option>
      <option value="VIDEO_CONFERENCE">{_t("Virtual Events")}</option>
      <option value="CORPORATE_OFFICE">{_t("Physical Location")}</option>
    </Form.Control>
  );
};

export const ThemeFilterControls = () => {
  const dispatch = useDispatch();
  const qsTheme = useQueryParams().eventTheme || "";
  const [keyword, setKeyword] = useState();
  const debouncedKeyword = useDebounce(keyword, 500);
  const [autocompleteThemes] = useFetchAutocomplete(debouncedKeyword ?? "", THEME_SUBTHEME);
  const initialThemesFilterValue = useSelector((state) => state.misc.initialThemesFilter);

  useEffect(() => {
    fetchDefaultThemes();
  }, []);

  const fetchDefaultThemes = async () => {
    if (!initialThemesFilterValue) {
      const data = await tryDoFetch(ALL_AUTOCOMPLETE_URL + THEME_SUBTHEME, null, "GET");
      let initialTheme = data?.hits?.hits?.map((el) => el._source.name) || [];
      if (initialTheme) {
        initialTheme.unshift(ALL_THEMES);
        dispatch(miscSlice.actions.set({ key: INITIAL_THEMES_FILTER, value: initialTheme }));
      }
    }
  };

  const onSelectSuggestion = (theme) => () => {
    if (theme.value === ALL_THEMES) {
      setKeyword(undefined);
      if (qsTheme) push(setParam(EVENT_THEME, ""));
    } else {
      push(setParam(EVENT_THEME, theme.value));
    }
  };

  return (
    <AutoComplete
      placeholder={ALL_THEMES}
      keyword={keyword ?? qsTheme}
      setKeyword={setKeyword}
      suggestions={keyword ? autocompleteThemes : (initialThemesFilterValue ?? [])}
      onSelectSuggestion={onSelectSuggestion}
      suffix={true}
    />
  );
};
